import { config } from '@uhg-abyss/web/tools/config';
import { find } from 'lodash';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  Constants,
  GQL_API_NAMES,
  ReverseCoverageTypesCodes,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { buildBhGroupedAoeCategoriesCode } from '../../common/ContentFilters/AllFilterDrawer/ProviderFilters/GroupedAoeFilter/groupedAoe.utils';
import { isMissingParameters } from '../../errors/helpers';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { isPolicyIdPresentForAOESuppression } from '../../utils/common';
import { getProviderFilterOptions } from '../../utils/providerSearch.utils';
import {
  getAdditionalAttributes,
  getAoeFiltersFromLagoon,
  getCurrentMember,
} from '../../utils/user.utils';
import { useCustomQuery } from '../useCustomQuery';
import { useFeatureFlag } from '../useFeatureFlag';
import { useLagoon } from '../useLagoon';
import { useProviderSearchVariables } from '../useProviderSearch/useProviderSearch';
import GET_PROVIDER_FILTER_COUNT_V2 from './ProviderFilterCountV2.graphql';

export const useProviderFilterCountV2 = (options) => {
  const [filterOptions, setFilterOptions] = useState({});
  const [queryResult, queryAction] = useCustomQuery(
    GET_PROVIDER_FILTER_COUNT_V2,
    {
      ...options,
      url: config('GRAPHQL_API_URL'),
      accessor: 'providerFilterCountV2',
      onCompleted: (result) => {
        const data = result?.data?.providerFilterCountV2;
        if (options?.onCompleted) {
          options.onCompleted(data);
        }
        setFilterOptions(data);
      },
    }
  );

  const parsedResult = {
    ...queryResult,
    data: filterOptions,
  };

  return [parsedResult, queryAction];
};

export const useParsedProviderFiltersV2 = (params) => {
  const [providerFilters, getProviderFilters] = useProviderFilterCountV2({});
  const featureSuppression = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_SUPPRESSION
  )();
  const suppressedAOECodesRow = find(featureSuppression, {
    DNT_feature:
      ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.GENDER_AFFIRMING_AOE_CODES,
    active: true,
  });
  const {
    sectionType,
    providerType,
    validPlanVariationCode,
    providerSearchVariables: variables,
    featureFlags,
    canMakeProviderSearchQuery: canMakeProviderFilterCountQuery,
    requiredParamsForProviderFilterCountQuery: requiredParams,
  } = useProviderSearchVariables(params);

  const [enableAgesTreatedFilterFlag, enableBhGroupedAoeFilter] =
    useFeatureFlag([
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_AGES_TREATED_FILTER,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_GROUPED_AOE_FILTER,
    ]);
  const allConfigs = useLagoon('config');
  const aoeFiltersLagoon = useLagoon(Constants.LAGOON_TABLE.AOE_FILTERS)();

  const aoeFilters = getAoeFiltersFromLagoon(
    aoeFiltersLagoon,
    providerType,
    enableAgesTreatedFilterFlag
  );

  const additionalAttributesLagoon = useLagoon('additional-attributes')();
  const currentMember = getCurrentMember();
  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      storeLob: state.lob,
      chipValue: state.chipValue,
    }))
  );

  const additionalAttributes =
    getAdditionalAttributes(
      additionalAttributesLagoon,
      currentMember,
      coverageType,
      providerType,
      GQL_API_NAMES.PROVIDER_FILTER_COUNT
    ) || [];

  useEffect(() => {
    if (!canMakeProviderFilterCountQuery) {
      return;
    }
    if (!(providerType || sectionType)) {
      return;
    }

    const pesVersion = params.pesVersion;
    const isPesV2 = pesVersion === '2';

    if (pesVersion && !isPesV2) {
      if (!validPlanVariationCode) {
        throw new Error('Plan Variation Code is required for pesVersion "1"');
      }
      if (!variables.policyID) {
        throw new Error('policyID is required for pesVersion "1"');
      }
    }

    if (isMissingParameters('Provider Filter Count V2', requiredParams)) {
      return;
    }

    const { keyword, ...variablesWithoutKeyword } = variables;
    const keywordArray = keyword ? [keyword] : [];
    const keywords = variables.keywords || keywordArray;

    const v2Variables = {
      ...variablesWithoutKeyword,
      additionalAttributes,
      aoeFiltersTable: aoeFilters,
      portalSource: 'psx',
      suppressAOECodes: isPolicyIdPresentForAOESuppression(
        coverageType,
        suppressedAOECodesRow,
        currentMember?.policyNumber ?? ''
      ),
      ...(params.pesVersion && { pesVersion: params.pesVersion }),
      providerTypes: providerType ? [providerType] : ['PRACTITIONER'],
      coverageTypes: requiredParams.coverages
        ? [
            {
              coverageType: requiredParams.coverageType || 'M',
              reciprocityIds: [requiredParams.reciprocityId || ''],
            },
          ]
        : [],
      ...(keywords?.length > 0 && { keywords }),
    };

    const areaOfExpertise = (v2Variables?.aoeCodes || []).map((value) =>
      String(value)
    );
    const isBhGroupedAoeCoverage =
      coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
      coverageType === ReverseCoverageTypesCodes['BEHAVIORAL CARVE-OUT'];
    const aoeCategoriesCode =
      enableBhGroupedAoeFilter &&
      isBhGroupedAoeCoverage &&
      areaOfExpertise?.length
        ? buildBhGroupedAoeCategoriesCode(areaOfExpertise, allConfigs)
        : '';

    if (aoeCategoriesCode) {
      v2Variables.aoeCodes = [];
      v2Variables.aoeCategoriesCode = aoeCategoriesCode;
    }

    getProviderFilters({ variables: v2Variables });
  }, [
    JSON.stringify(variables),
    JSON.stringify(additionalAttributes),
    canMakeProviderFilterCountQuery,
  ]);

  const filtersByProviderType =
    providerFilters?.data?.filtersByProviderType || [];
  const totalCountByProviderType =
    providerFilters?.data?.totalCountByProviderType || [];

  const primaryFilters = filtersByProviderType[0] || {};
  const primaryCount = totalCountByProviderType[0]?.count || 0;

  return {
    error: providerFilters?.error,
    isLoading: providerFilters?.isLoading,
    totalCount: primaryCount,
    providerFilterList: getProviderFilterOptions(
      primaryFilters.providerFilterList,
      featureFlags
    ),
    filtersByProviderType: filtersByProviderType.map((filters) => ({
      ...filters,
      providerFilterList: getProviderFilterOptions(
        filters.providerFilterList,
        featureFlags
      ),
    })),
    totalCountByProviderType,
  };
};
